import React, { useState, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Switch,
  Text,
  Button,
  IconButton,
  Flex,
  Spacer,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import { SettingsIcon } from '@chakra-ui/icons';
import ReactApexChart from 'react-apexcharts';
import useChart from './services/useChart';
import { getRandomColor } from './utils';

function BanksChart({ env, startedFrom, finishedTo, daysList, textColor, banks }) {
  const [categories, setCategories] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = React.useRef();

  const { chartOptions, chartData } = useChart({
    env,
    startedFrom,
    finishedTo,
    daysList,
    categories: activeCategories,
    groupBy: 'provider_wallet_id,type',
    nameFormatter: category => category.name,
  });

  useEffect(() => {
    const initCategories = async () => {
      const categories = banks.reduce((accum, bank) => {
        const category = {
          ...bank,
          provider_wallet_id: bank.id,
          chartColor: getRandomColor(),
        };

        accum.push(Object.assign({ type: 'payment' }, category));
        accum.push(Object.assign({ type: 'payout' }, category));

        return accum;
      }, []);

      categories.push({
        provider_wallet_id: 'unknown',
        name: 'Не определен',
        provider: 'Не определен',
        type: 'payment',
        chartColor: '#718096',
      });

      categories.push({
        provider_wallet_id: 'unknown',
        name: 'Не определен',
        provider: 'Не определен',
        type: 'payout',
        chartColor: '#D69E2E',
      });

      setCategories(categories);
      setActiveCategories(categories);
    };

    initCategories();
  }, [banks]);

  function handleSwitchChange(category) {
    setActiveCategories(prevState => {
      if (prevState.includes(category)) {
        return prevState.filter(item => item !== category);
      } else {
        return [...prevState, category];
      }
    });
  }

  function resetDisabledCategories() {
    setActiveCategories(categories);
    onClose();
  }

  return (
    <>
      <Flex>
        <Text fontSize="xl" color={textColor} fontWeight="bold">
          Банки
        </Text>
        <Spacer />
        <IconButton
          size="md"
          colorScheme="teal"
          ref={btnRef}
          icon={<SettingsIcon />}
          onClick={onOpen}
        ></IconButton>
      </Flex>

      <ReactApexChart
        options={chartOptions}
        series={chartData}
        type="area"
        height={400}
      />

      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        finalFocusRef={btnRef}
        size={'lg'}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Настройки графика</DrawerHeader>

          <DrawerBody>
            {categories.map((category, idx) => {
              return (
                <FormControl
                  key={idx}
                  display="flex"
                  alignItems="center"
                  mb={5}
                >
                  <FormLabel htmlFor={`category-${idx}`} whiteSpace="nowrap">
                    {`${category.name} - ${category.provider} (${category.type})`}
                  </FormLabel>
                  <Switch
                    id={`category-${idx}`}
                    size="lg"
                    color={category.chartColor}
                    isChecked={activeCategories.includes(category)}
                    onChange={() => handleSwitchChange(category)}
                  />
                </FormControl>
              );
            })}
          </DrawerBody>

          <DrawerFooter>
            {categories.length !== activeCategories.length && (
              <Button
                variant="outline"
                mr={3}
                onClick={resetDisabledCategories}
              >
                Сбросить
              </Button>
            )}

            <Button colorScheme="blue" onClick={onClose}>
              Закрыть
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default React.memo(BanksChart);
